import React, { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useDebounce } from '../../../common/Debounce.hooks';
import { useFindUsers } from '../../../user/User.hooks';
import { User } from '../../../user/User.interface';
import { useAddUsersToGroup } from '../Group.hooks';
import Alert from '@material-ui/lab/Alert';
import { ConfirmDialog } from '@sierralabs/react-material-ui';
export interface GroupAssignDialogProps {
  groupId: number;
  open: boolean;
  onCancel?: () => void;
  onAssigned: () => void;
}

const GroupUserAssignDialog: React.FC<GroupAssignDialogProps> = ({
  groupId,
  open,
  onCancel,
  onAssigned
}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search || '', 300);
  const previousSearch = useRef('');
  const { userList, isLoading, get } = useFindUsers();
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [confirmDialog, setConfirmDialog] = useState(false);

  const {
    data: result,
    isLoading: isUpdating,
    error: updateError,
    addUsersToGroup
  } = useAddUsersToGroup(groupId);

  useEffect(() => {
    // handle searching
    if (debouncedSearch !== previousSearch.current) {
      previousSearch.current = debouncedSearch;
    }
    const params = {
      filter: debouncedSearch,
      withRoles: true
    } as any;
    get(params);
  }, [debouncedSearch, get]);

  useEffect(() => {
    // effect after users assigned to group
    if (result && !isUpdating) {
      console.log('result', result);
      onAssigned();
    }
  }, [result, isUpdating, onAssigned]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Add Employees to Team</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Search for an employee that you would like to assign to this team.
        </DialogContentText>
        <Box mt={1} mb={2}>
          <Autocomplete
            multiple
            id='user-selector'
            options={userList?.data || []}
            getOptionLabel={user => `${user.firstName} ${user.lastName}`}
            renderInput={params => (
              <TextField
                {...params}
                variant='outlined'
                fullWidth
                placeholder='Search Employees'
              />
            )}
            renderOption={option => {
              return (
                <Grid container alignItems='center'>
                  <Grid item xs>
                    {option.firstName} {option.lastName}
                    <Typography variant='body2' color='textSecondary'>
                      {option.roles?.map(r => r.name).join(', ')}
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    {option.email}
                    <Typography variant='body2' color='textSecondary'>
                      {option.groups?.map(g => g.name).join(', ')}
                    </Typography>
                  </Grid>
                </Grid>
              );
            }}
            onInputChange={(event, newInputValue) => setSearch(newInputValue)}
            onChange={(event, value) => {
              setSelectedUsers(value);
            }}
          />
        </Box>
        {updateError && (
          <Alert severity='error'>
            Error occurred.{' '}
            {updateError.message ||
              'Please check your internet connection and try again.'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant='contained'
          color='primary'
          disabled={isLoading || isUpdating || selectedUsers.length <= 0}
          endIcon={isUpdating && <CircularProgress color='primary' size={20} />}
          onClick={() => setConfirmDialog(true)}
        >
          Assign
        </Button>
      </DialogActions>
      {confirmDialog && (
        <ConfirmDialog
          open
          title='Update User Roles'
          message='Would you like to update the user roles with these new settings?'
          cancelLabel='No'
          confirmLabel='Yes'
          onCancel={() => {
            addUsersToGroup(
              selectedUsers.map(u => u.id),
              false
            );
          }}
          onConfirm={() => {
            addUsersToGroup(
              selectedUsers.map(u => u.id),
              true
            );
          }}
        />
      )}
    </Dialog>
  );
};

export default GroupUserAssignDialog;
